/**
 * knowledgeBase.js — ฐานความรู้ของผู้ช่วยตอบคำถาม HRVEC (HrvecChatbot.vue)
 *
 * แต่ละ intent คือ "หนึ่งประเด็นคำถาม" ประกอบด้วย
 *   id        : รหัสอ้างอิง (ใช้ใน related / quickReplies / log)
 *   category  : รหัสหมวด (ดู CATEGORIES)
 *   title     : หัวข้อที่แสดงเป็นปุ่มคำถามแนะนำ
 *   keywords  : คำ/วลีที่ผู้ใช้มักพิมพ์ (รวมคำพ้อง คำสะกดผิดที่พบบ่อย) — ยิ่งยาว/เฉพาะเจาะจง ยิ่งได้คะแนนสูง
 *   roles     : (ไม่บังคับ) กลุ่มผู้ใช้ที่ประเด็นนี้เกี่ยวข้องโดยตรง ใช้เพิ่มน้ำหนัก + แสดงเป็นคำถามแนะนำ
 *   answer    : ข้อความตอบหลัก รองรับ **ตัวหนา** และขึ้นบรรทัดใหม่ด้วย \n
 *   steps     : (ไม่บังคับ) ขั้นตอนเป็นข้อๆ
 *   tip       : (ไม่บังคับ) ข้อควรระวัง/หมายเหตุ
 *   links     : (ไม่บังคับ) ปุ่มพาไปหน้าจอ { label, to, roles? } — roles กรองให้เห็นเฉพาะกลุ่มที่เข้าเมนูนั้นได้จริง
 *   related   : (ไม่บังคับ) id ของประเด็นที่มักถามต่อ
 *
 * เนื้อหาอ้างอิงพฤติกรรมจริงของระบบ ณ ปัจจุบัน (login.php, UsageGuideDialog.vue, คู่มือการใช้งานระบบ_HRVEC.md,
 * router.js) — เมื่อระบบเปลี่ยน (เมนู/ขั้นตอน/เงื่อนไข) ต้องแก้ข้อความในไฟล์นี้ให้ตรงด้วย
 * ห้ามใส่ข้อมูลส่วนบุคคลจริง หรือเบอร์โทร/อีเมลที่ยังไม่ได้รับการยืนยันจากหน่วยงาน
 */

// ---------------------------------------------------------------------------
// กลุ่มผู้ใช้งาน (ได้จาก localStorage "user" — ดู detectRole() ใน chatEngine.js)
// ---------------------------------------------------------------------------
export const ROLE_LABELS = {
  guest: "ผู้ใช้งานทั่วไป (ยังไม่เข้าสู่ระบบ)",
  teacher: "ข้าราชการครูและบุคลากรทางการศึกษา",
  gov_em: "พนักงานราชการ",
  etc: "บุคลากรทางการศึกษาอื่น (ครูอัตราจ้าง/ลูกจ้าง)",
  A: "ผู้ดูแลระบบส่วนกลาง",
  B: "สถานศึกษา",
  C: "สถาบันการอาชีวศึกษา",
  G: "สำนักงานอาชีวศึกษาจังหวัด (สอจ.)",
  S: "ศึกษานิเทศก์/หน่วยตรวจราชการ"
};

const SELF = ["teacher", "gov_em"]; // เมนูใต้ /user
const UNIT = ["B", "C", "G", "S"]; // บัญชีหน่วยงาน

export const CATEGORIES = [
  { id: "login", label: "เข้าสู่ระบบ", icon: "mdi-login" },
  { id: "password", label: "รหัสผ่าน", icon: "mdi-lock-reset" },
  { id: "thaid", label: "ThaiD", icon: "mdi-qrcode-scan" },
  { id: "profile", label: "ข้อมูลส่วนตัว", icon: "mdi-account-edit" },
  { id: "transfer", label: "การย้าย", icon: "mdi-swap-horizontal" },
  { id: "assistant", label: "ครูผู้ช่วย", icon: "mdi-account-school" },
  { id: "exam", label: "สอบคัดเลือก", icon: "mdi-clipboard-text" },
  { id: "govem", label: "พนักงานราชการ", icon: "mdi-badge-account" },
  { id: "manpower", label: "อัตรากำลัง", icon: "mdi-chart-bar" },
  { id: "college", label: "งานสถานศึกษา", icon: "mdi-domain" },
  { id: "admin", label: "ผู้ดูแลระบบ", icon: "mdi-shield-account" },
  { id: "general", label: "ทั่วไป", icon: "mdi-help-circle" }
];

// ---------------------------------------------------------------------------
// คำถามแนะนำเริ่มต้นตามกลุ่มผู้ใช้ (ปุ่มลัดในหน้าแรกของแชท)
// ---------------------------------------------------------------------------
export const QUICK_REPLIES = {
  guest: ["login_which_card", "login_failed", "password_forgot", "thaid_steps", "assistant_report", "general_manual"],
  teacher: ["profile_edit", "transfer_teacher_apply", "password_change", "exam_apply", "profile_resume", "scout_update"],
  gov_em: ["profile_edit", "transfer_govem_apply", "govem_assessment_history", "password_change", "profile_resume"],
  etc: ["profile_edit", "password_change", "scout_update", "etc_no_thaid"],
  A: ["admin_user_manage", "admin_timeline", "admin_transfer_process", "admin_switch_user", "admin_security", "admin_chatbot_insights"],
  B: ["college_reset_password", "college_add_personnel", "college_responsible_person", "manpower_report", "college_transfer_conditions", "assistant_committee"],
  C: ["unit_personnel_view", "manpower_report", "unit_transfer_review", "govem_unit", "exam_unit_assess"],
  G: ["unit_personnel_view", "manpower_report", "unit_transfer_review", "exam_unit_assess"],
  S: ["unit_personnel_view", "manpower_report", "unit_transfer_review"]
};

// ---------------------------------------------------------------------------
// ฐานความรู้
// ---------------------------------------------------------------------------
export const INTENTS = [
  // ===================== ทักทาย / สนทนาทั่วไป =====================
  {
    id: "greeting",
    category: "general",
    title: "สวัสดี",
    keywords: ["สวัสดี", "หวัดดี", "ดีครับ", "ดีค่ะ", "hello", "hi", "ทักทาย", "มีใครอยู่ไหม", "ขอสอบถาม", "สอบถามหน่อย"],
    answer:
      "สวัสดีครับ 🙏 ผมคือผู้ช่วยตอบคำถามระบบ HRVEC\nพิมพ์คำถามได้เลย เช่น \"ลืมรหัสผ่าน\" \"ยื่นย้ายยังไง\" \"ThaiD สแกนไม่ผ่าน\" หรือเลือกหัวข้อด้านล่างครับ"
  },
  {
    id: "thanks",
    category: "general",
    title: "ขอบคุณ",
    keywords: ["ขอบคุณ", "ขอบใจ", "thank", "thx", "เข้าใจแล้ว", "โอเค", "ok", "ได้แล้ว", "เรียบร้อย"],
    answer: "ยินดีครับ 😊 หากมีคำถามเพิ่มเติม พิมพ์ถามได้ตลอดเวลาครับ"
  },
  {
    id: "general_about",
    category: "general",
    title: "HRVEC คือระบบอะไร",
    keywords: ["hrvec คือ", "ระบบนี้คือ", "ระบบอะไร", "ใช้ทำอะไร", "เกี่ยวกับระบบ", "ระบบบริหารงานบุคคล", "ระบบมีอะไรบ้าง"],
    answer:
      "**HRVEC** คือระบบบริหารงานบุคคล สำนักอำนวยการ สำนักงานคณะกรรมการการอาชีวศึกษา (สอศ.) ครอบคลุม 10 งานหลัก:",
    steps: [
      "บริหารงานบุคคล (ข้อมูลข้าราชการครู บุคลากรฯ พนักงานราชการ)",
      "งานอัตรากำลังสถานศึกษา และข้อมูลลูกจ้าง",
      "ข้อมูลสถานศึกษา สถาบัน สอจ.",
      "กรอบอัตรากำลังข้าราชการครู (จ.18)",
      "กรอบอัตรากำลังพนักงานราชการ",
      "การย้ายข้าราชการครู / พนักงานราชการ",
      "การย้ายผู้บริหาร (ผอ. / รอง ผอ.)",
      "การรายงานตัวครูผู้ช่วยและตำแหน่งว่าง",
      "การประเมินครูผู้ช่วยและแต่งตั้งคณะกรรมการ",
      "การเบิกจ่ายค่าตอบแทนพนักงานราชการ"
    ],
    related: ["login_which_card", "general_manual"]
  },
  {
    id: "general_manual",
    category: "general",
    title: "คู่มือการใช้งาน / ประกาศ",
    keywords: ["คู่มือ", "manual", "วิธีใช้", "ประกาศ", "ข่าว", "หลักเกณฑ์", "เกณฑ์", "ข้อกำหนด", "เอกสารประกอบ", "ดาวน์โหลดคู่มือ"],
    answer:
      "คู่มือการใช้งาน ข่าวประชาสัมพันธ์ ข้อกำหนด และหลักเกณฑ์ต่างๆ (เช่น หลักเกณฑ์การย้าย) รวมอยู่ที่หน้า **\"ข่าวประชาสัมพันธ์ ข้อกำหนด เกณฑ์\"** ครับ",
    links: [{ label: "เปิดหน้าคู่มือ/ประกาศ", to: "/news" }]
  },
  {
    id: "general_contact",
    category: "general",
    title: "ติดต่อเจ้าหน้าที่",
    keywords: ["ติดต่อ", "เจ้าหน้าที่", "แอดมิน", "admin", "คุยกับคน", "ขอคุยกับเจ้าหน้าที่", "เบอร์โทร", "โทรหา", "แจ้งปัญหา", "ร้องเรียน", "ผู้ดูแล"],
    answer:
      "ลำดับการติดต่อที่แนะนำ เพื่อให้ได้รับความช่วยเหลือเร็วที่สุด:",
    steps: [
      "**บุคลากร (ครู/พนักงานราชการ/ลูกจ้าง)** → ติดต่องานบุคลากรหรือผู้ดูแลระบบ HRVEC ของ**สถานศึกษาต้นสังกัด**ก่อน (รีเซ็ตรหัสผ่าน แก้ไขข้อมูลที่ตนเองแก้ไม่ได้)",
      "**สถานศึกษา/สถาบัน/สอจ.** → ติดต่อผู้ดูแลระบบส่วนกลาง สำนักอำนวยการ สอศ.",
      "แจ้งรายละเอียดให้ครบ: ชื่อหน่วยงาน, เมนูที่ใช้งาน, ข้อความ error ที่พบ และภาพหน้าจอ (ห้ามส่งรหัสผ่าน)"
    ],
    tip: "ช่องทางติดต่อส่วนกลางที่เป็นปัจจุบันดูได้จากหน้าข่าวประชาสัมพันธ์/คู่มือของระบบ",
    links: [{ label: "หน้าข่าวประชาสัมพันธ์/คู่มือ", to: "/news" }]
  },

  // ===================== เข้าสู่ระบบ =====================
  {
    id: "login_which_card",
    category: "login",
    title: "ต้องเข้าสู่ระบบช่องไหน",
    keywords: ["เข้าช่องไหน", "ปุ่มไหน", "การ์ดไหน", "เลือกกลุ่ม", "กลุ่มไหน", "ประเภทผู้ใช้", "เข้าระบบตรงไหน", "login ตรงไหน", "เข้าสู่ระบบที่ไหน", "วิธีเข้าสู่ระบบ", "เข้าระบบยังไง", "เข้าใช้งาน"],
    answer: "หน้าแรกของระบบมีช่องเข้าสู่ระบบ 4 กลุ่ม เลือกตามสถานะของท่าน:",
    steps: [
      "**ข้าราชการครูและบุคลากรทางการศึกษา** (ครู ผอ. รอง ผอ.) → ใช้เลขบัตรประชาชน + ยืนยัน ThaiD",
      "**ผู้ดูแลระบบ / สถานศึกษา / สถาบัน / สอจ.** → ใช้รหัสหน่วยงาน + ยืนยัน ThaiD ของตัวผู้ใช้เอง",
      "**พนักงานราชการ** → ใช้เลขบัตรประชาชน + ยืนยัน ThaiD",
      "**บุคลากรทางการศึกษาอื่น** (ครูอัตราจ้าง ลูกจ้างประจำ ลูกจ้างชั่วคราว) → ใช้เลขบัตรประชาชน/Passport/รหัสประจำตัว (ยังไม่ต้องยืนยัน ThaiD)"
    ],
    tip: "ไม่แน่ใจว่าเป็นกลุ่มใด ติดต่องานบุคลากรของสถานศึกษาท่าน",
    links: [{ label: "ไปหน้าเข้าสู่ระบบ", to: "/pages/login", roles: ["guest"] }],
    related: ["login_failed", "thaid_steps"]
  },
  {
    id: "login_failed",
    category: "login",
    title: "เข้าสู่ระบบไม่ได้",
    keywords: ["เข้าระบบไม่ได้", "เข้าสู่ระบบไม่ได้", "login ไม่ได้", "ล็อกอินไม่ได้", "ล๊อกอินไม่ได้", "ล็อคอินไม่ได้", "เข้าไม่ได้", "รหัสผ่านไม่ถูกต้อง", "ชื่อผู้ใช้ไม่ถูกต้อง", "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง", "ใช้งานไม่ได้", "ระบบล่ม", "เว็บเข้าไม่ได้"],
    answer: "ลองตรวจสอบตามลำดับนี้ครับ:",
    steps: [
      "เลือกช่องเข้าสู่ระบบ**ให้ตรงกลุ่ม** (ครู / หน่วยงาน / พนักงานราชการ / บุคลากรอื่น) — เลือกผิดช่องจะขึ้นว่ารหัสไม่ถูกต้อง",
      "ตรวจว่าพิมพ์เลขบัตรประชาชนครบ 13 หลัก ไม่มีขีด/ช่องว่าง และปิด Caps Lock / เปลี่ยนภาษาแป้นพิมพ์เป็นภาษาอังกฤษ",
      "ดูกล่อง **\"ตรวจสอบความพร้อมของระบบ\"** ด้านบนหน้าเข้าสู่ระบบ จะบอกว่าปัญหาอยู่ที่อินเทอร์เน็ต เซิร์ฟเวอร์ ThaiD หรือเวลาเครื่อง",
      "จำรหัสผ่านไม่ได้ → กด **\"ลืมรหัสผ่าน\"** ใต้ฟอร์ม",
      "ใส่ผิดหลายครั้งจนถูกล็อก → รอประมาณ 15 นาทีแล้วลองใหม่"
    ],
    links: [{ label: "ไปหน้าเข้าสู่ระบบ", to: "/pages/login", roles: ["guest"] }],
    related: ["login_locked", "password_forgot", "login_which_card", "login_browser"]
  },
  {
    id: "login_locked",
    category: "login",
    title: "บัญชีถูกล็อก / ล็อกอินผิดเกินกำหนด",
    keywords: ["ถูกล็อก", "ถูกล็อค", "ล็อกบัญชี", "บัญชีล็อก", "ผิดเกินกำหนด", "ล็อกอินผิดเกินกำหนด", "รออีก", "ผิดหลายครั้ง", "locked", "โดนล็อก", "โดนล็อค"],
    answer:
      "หากกรอกรหัสผ่านผิด **5 ครั้งภายใน 15 นาที** ระบบจะล็อกการเข้าสู่ระบบของบัญชีนั้นชั่วคราว เพื่อป้องกันการเดารหัสผ่าน\nให้**รอตามเวลาที่ระบบแจ้ง (ประมาณ 15 นาที)** แล้วลองใหม่ด้วยรหัสผ่านที่ถูกต้อง หรือใช้เมนู \"ลืมรหัสผ่าน\" เพื่อตั้งรหัสใหม่",
    tip: "หากไม่ได้เป็นผู้พยายามเข้าสู่ระบบเอง อาจมีผู้อื่นพยายามเข้าบัญชีท่าน ควรเปลี่ยนรหัสผ่านและแจ้งผู้ดูแลระบบ",
    related: ["password_forgot", "security_suspect"]
  },
  {
    id: "login_session_expired",
    category: "login",
    title: "Session หมดอายุ",
    keywords: ["session หมดอายุ", "เซสชัน", "เซสชั่น", "หมดอายุ", "เด้งออก", "ถูกเตะออก", "หลุดออกจากระบบ", "ออกจากระบบเอง", "ให้เข้าสู่ระบบใหม่", "401", "ยังไม่ได้เข้าสู่ระบบ"],
    answer:
      "เพื่อความปลอดภัย การเข้าสู่ระบบแต่ละครั้งมีอายุ **8 ชั่วโมง** เมื่อขึ้นข้อความ \"Session หมดอายุ หรือยังไม่ได้เข้าสู่ระบบ\" ระบบจะพาไปหน้าเข้าสู่ระบบ\n**ต้องกรอกชื่อผู้ใช้/รหัสผ่านและเข้าสู่ระบบใหม่ด้วยตนเอง** — การกด \"ตกลง\" ที่กล่องแจ้งเตือนอย่างเดียวไม่ได้เข้าสู่ระบบให้อัตโนมัติ",
    tip: "หากกำลังกรอกข้อมูลยาวๆ ควรกดบันทึกเป็นระยะ เพื่อไม่ให้ข้อมูลหายเมื่อ Session หมดอายุ",
    links: [{ label: "เข้าสู่ระบบใหม่", to: "/pages/login" }]
  },
  {
    id: "login_browser",
    category: "login",
    title: "หน้าเว็บค้าง / แสดงผลผิดปกติ",
    keywords: ["เว็บค้าง", "หน้าขาว", "จอขาว", "เว็บขาว", "หน้าเว็บ", "โหลดไม่ขึ้น", "หมุนค้าง", "แสดงผลผิดปกติ", "ปุ่มกดไม่ได้", "กดไม่ติด", "browser", "เบราว์เซอร์", "บราวเซอร์", "ล้างแคช", "cache", "อัปเดตเวอร์ชัน", "เวอร์ชันใหม่", "ช้ามาก", "ช้า"],
    answer: "อาการหน้าเว็บค้างหรือแสดงผลผิดปกติ ส่วนใหญ่แก้ได้ดังนี้:",
    steps: [
      "กด **Ctrl + F5** (Mac: Cmd + Shift + R) เพื่อโหลดหน้าเว็บเวอร์ชันล่าสุด — โดยเฉพาะเมื่อมีแถบแจ้ง \"มีเวอร์ชันใหม่\" มุมขวาล่าง",
      "ใช้เบราว์เซอร์รุ่นปัจจุบัน เช่น Google Chrome หรือ Microsoft Edge",
      "ปิดโหมดที่บล็อก Cookies/localStorage (ระบบต้องใช้ในการจำการเข้าสู่ระบบ)",
      "ลองออกจากระบบแล้วเข้าสู่ระบบใหม่",
      "ยังไม่หาย → ตรวจกล่อง \"ตรวจสอบความพร้อมของระบบ\" ที่หน้าเข้าสู่ระบบ แล้วแจ้งผู้ดูแลพร้อมภาพหน้าจอ"
    ],
    related: ["general_contact", "print_problem"]
  },
  {
    id: "login_no_permission",
    category: "login",
    title: "ขึ้นว่า \"ไม่มีสิทธิ์\"",
    keywords: ["ไม่มีสิทธิ์", "ไม่มีสิทธิ", "สิทธิ์ไม่พอ", "403", "access denied", "กดแล้วไม่มีอะไรเกิดขึ้น", "เข้าเมนูไม่ได้", "ไม่เห็นเมนู", "เมนูหาย", "ไม่มีเมนู"],
    answer:
      "ข้อความ \"ไม่มีสิทธิ์\" หมายความว่าบัญชีที่ใช้อยู่ไม่มีสิทธิ์ในรายการนั้น เช่น เป็นข้อมูลของสถานศึกษาอื่น หรือเป็นเมนูของบัญชีระดับอื่น\nระบบตรวจสิทธิ์จากบัญชีที่เข้าสู่ระบบจริงทุกครั้ง บางเมนูเปิดให้ใช้เฉพาะ**ช่วงเวลาที่ส่วนกลางกำหนด** (เช่น ช่วงยื่นย้าย/รายงานตัว)",
    steps: [
      "ตรวจว่ากำลังทำรายการของหน่วยงาน/ของตนเองอยู่",
      "ตรวจว่าอยู่ในช่วงเวลาที่เปิดให้ดำเนินการ (ดูประกาศ)",
      "หากคิดว่าควรมีสิทธิ์ ติดต่อผู้ดูแลระบบของหน่วยงาน"
    ],
    related: ["general_contact", "general_manual"]
  },
  {
    id: "college_responsible_person",
    category: "login",
    title: "หน้าต่าง \"กำหนดผู้รับผิดชอบระบบ\"",
    keywords: ["ผู้รับผิดชอบระบบ", "กำหนดผู้รับผิดชอบ", "ผู้รับผิดชอบ", "กรอกเลขบัตรผู้รับผิดชอบ", "ไม่พบข้อมูลบุคลากร", "ไม่พบเลขบัตร", "บังคับกรอกเลขบัตร"],
    roles: UNIT,
    answer:
      "บัญชีหน่วยงาน (สถานศึกษา/สถาบัน/สอจ.) ต้องกำหนด**ผู้รับผิดชอบระบบ** 1 ครั้ง โดยกรอกเลขบัตรประชาชน 13 หลักของเจ้าหน้าที่ที่ดูแลระบบจริง",
    steps: [
      "เลขบัตรที่กรอกต้องเป็นบุคลากรที่**มีข้อมูลอยู่ในระบบบุคลากรของหน่วยงานแล้ว**",
      "หากขึ้นว่าไม่พบข้อมูล → ตรวจเลข 13 หลักอีกครั้ง หรือเพิ่มข้อมูลบุคลากรท่านนั้นในเมนูข้อมูลบุคลากรของสถานศึกษาก่อน",
      "กด \"บันทึก\" — ระบบจะไม่ถามซ้ำ ต้องการเปลี่ยนผู้รับผิดชอบให้ติดต่อผู้ดูแลระบบส่วนกลาง"
    ],
    related: ["college_add_personnel", "thaid_unit"]
  },

  // ===================== ThaiD =====================
  {
    id: "thaid_steps",
    category: "thaid",
    title: "ขั้นตอนยืนยันตัวตนผ่าน ThaiD",
    keywords: ["thaid", "ยืนยันตัวตน", "สแกน qr", "qr code", "คิวอาร์", "สแกนคิวอาร์", "ขั้นตอน thaid", "แอป thaid", "ยืนยันตัวตนดิจิทัล", "dopa", "กรมการปกครอง", "2fa"],
    answer:
      "ข้าราชการครูฯ พนักงานราชการ และบัญชีหน่วยงาน ต้องยืนยันตัวตนผ่านแอป **ThaiD** (กรมการปกครอง) ต่อจากการกรอกรหัสผ่าน:",
    steps: [
      "กรอกชื่อผู้ใช้/รหัสผ่านแล้วกด \"เข้าสู่ระบบ\" ระบบจะพาไปหน้าเว็บ ThaiD อัตโนมัติ",
      "หน้าเว็บจะแสดง **QR Code**",
      "เปิดแอป ThaiD บนมือถือ → เลือก **สแกน QR Code** → สแกนรหัสบนจอคอมพิวเตอร์",
      "ยืนยันด้วย **PIN 6 หลัก** ของแอป ThaiD",
      "ระบบจะพากลับเข้า HRVEC อัตโนมัติ ไม่ต้องกรอกข้อมูลซ้ำ"
    ],
    tip: "เลขบัตรประชาชนใน ThaiD ต้องเป็นบุคคลเดียวกับบัญชีที่เข้าสู่ระบบ (บัญชีหน่วยงาน: ต้องเป็นบุคลากรของหน่วยงานนั้น)",
    related: ["thaid_failed", "thaid_install", "thaid_why"]
  },
  {
    id: "thaid_failed",
    category: "thaid",
    title: "ยืนยัน ThaiD ไม่สำเร็จ",
    keywords: ["thaid ไม่ผ่าน", "ยืนยันตัวตนไม่สำเร็จ", "ยืนยันไม่ผ่าน", "สแกนไม่ผ่าน", "สแกนแล้วไม่เข้า", "ยืนยันตัวตนไม่ผ่าน", "thaid error", "เลขบัตรไม่ตรง", "qr หมดอายุ", "thaid ไม่ได้"],
    answer: "ตรวจสอบตามนี้ครับ:",
    steps: [
      "กด \"เข้าสู่ระบบใหม่\" แล้วทำขั้นตอนอีกครั้ง (QR Code มีอายุจำกัด)",
      "ตรวจว่า**เลขบัตรประชาชนในแอป ThaiD ตรงกับบัญชีที่ใช้ login** — ใช้ ThaiD ของคนอื่นยืนยันแทนไม่ได้",
      "บัญชีหน่วยงาน: ผู้ยืนยันต้องมีข้อมูลอยู่ในระบบบุคลากรของหน่วยงานนั้น หากยังไม่มี ให้งานบุคลากรเพิ่มข้อมูลก่อน",
      "ตรวจเวลาและวันที่ของคอมพิวเตอร์ให้ตรงปัจจุบัน (ดูในกล่องตรวจสอบความพร้อมของระบบ)",
      "ยังไม่สำเร็จ → ติดต่อผู้ดูแลระบบพร้อมภาพหน้าจอข้อความแจ้งเตือน"
    ],
    related: ["thaid_unit", "general_contact"]
  },
  {
    id: "thaid_install",
    category: "thaid",
    title: "ยังไม่มี/ยังไม่ได้ลงทะเบียนแอป ThaiD",
    keywords: ["ติดตั้ง thaid", "โหลด thaid", "ดาวน์โหลด thaid", "ไม่มี thaid", "ลงทะเบียน thaid", "สมัคร thaid", "ไม่มีแอป", "ไม่มีสมาร์ทโฟน", "ไม่มีมือถือ", "ลืม pin", "pin thaid"],
    answer:
      "ดาวน์โหลดแอป **\"ThaiD\"** จาก Play Store (Android) หรือ App Store (iOS) แล้วลงทะเบียนตามขั้นตอนของกรมการปกครอง ด้วยบัตรประชาชนของตนเอง จากนั้นกลับมาเข้าสู่ระบบ HRVEC อีกครั้ง",
    tip: "ปัญหาการลงทะเบียน/ลืม PIN ของแอป ThaiD เป็นบริการของกรมการปกครอง ต้องติดต่อผ่านช่องทางของกรมการปกครองโดยตรง",
    related: ["thaid_steps"]
  },
  {
    id: "thaid_why",
    category: "thaid",
    title: "ทำไมต้องใช้ ThaiD",
    keywords: ["ทำไมต้อง thaid", "ทำไมต้องยืนยันตัวตน", "ไม่อยากใช้ thaid", "ยกเลิก thaid", "ข้าม thaid", "บังคับ thaid"],
    answer:
      "เป็นการยกระดับความปลอดภัยตามนโยบายภาครัฐ แม้รหัสผ่านจะรั่วไหล ผู้อื่นก็เข้าสู่ระบบไม่ได้หากไม่มี ThaiD ของเจ้าของบัญชีจริง จึง**ไม่สามารถข้ามขั้นตอนนี้ได้**สำหรับกลุ่มที่ระบบกำหนด"
  },
  {
    id: "thaid_unit",
    category: "thaid",
    title: "บัญชีหน่วยงานยืนยัน ThaiD อย่างไร",
    keywords: ["หน่วยงาน thaid", "วิทยาลัย thaid", "สถานศึกษา thaid", "หลายคนใช้บัญชีเดียว", "เจ้าหน้าที่หลายคน", "ใช้ thaid ใคร", "ยืนยันแทน"],
    roles: ["A", ...UNIT],
    answer:
      "บัญชีหน่วยงานใช้รหัสหน่วยงานร่วมกัน แต่ขั้นตอน ThaiD ใช้ **ThaiD ของตัวผู้ใช้เอง** ระบบจะตรวจว่าเลขบัตรที่ยืนยันมีข้อมูลอยู่ในระบบบุคลากรของหน่วยงานนี้\nเจ้าหน้าที่หลายคนในหน่วยงานเดียวกันยืนยันด้วย ThaiD ของตนเองได้ ไม่จำกัดเฉพาะคนแรก",
    related: ["college_add_personnel", "thaid_failed"]
  },
  {
    id: "etc_no_thaid",
    category: "thaid",
    title: "ครูอัตราจ้าง/ลูกจ้าง ต้องใช้ ThaiD ไหม",
    keywords: ["ครูอัตราจ้าง", "ลูกจ้างชั่วคราว", "ลูกจ้างประจำ", "อัตราจ้าง thaid", "ลูกจ้าง thaid", "บุคลากรอื่น", "passport", "พาสปอร์ต", "ต่างชาติ"],
    answer:
      "กลุ่ม**บุคลากรทางการศึกษาอื่น** (ครูอัตราจ้าง ลูกจ้างประจำ ลูกจ้างชั่วคราว) ใช้เลขบัตรประชาชน / Passport / รหัสประจำตัว เป็นชื่อผู้ใช้ และ**ยังไม่ต้องยืนยันตัวตนผ่าน ThaiD** ในขณะนี้\nหากเข้าใช้งานครั้งแรกหรือจำรหัสไม่ได้ ให้กด \"ลืมรหัสผ่าน\" หรือขอให้สถานศึกษารีเซ็ตรหัสผ่านให้",
    related: ["password_forgot", "college_reset_password"]
  },

  // ===================== รหัสผ่าน =====================
  {
    id: "password_forgot",
    category: "password",
    title: "ลืมรหัสผ่าน",
    keywords: ["ลืมรหัส", "ลืมรหัสผ่าน", "ลืมพาสเวิร์ด", "ลืม password", "forgot password", "จำรหัสไม่ได้", "รีเซ็ตรหัส", "reset password", "ขอรหัสใหม่", "เข้าครั้งแรก", "ครั้งแรก", "รหัสผ่านเริ่มต้น", "รหัสเริ่มต้น"],
    answer: "กดปุ่ม **\"ลืมรหัสผ่าน\"** ใต้ฟอร์มเข้าสู่ระบบ แล้วทำตามขั้นตอน:",
    steps: [
      "กรอกเลขบัตรประชาชน (หรือรหัสหน่วยงาน) ชื่อ-นามสกุล และ**อีเมลที่บันทึกไว้ในระบบ** (บัญชีหน่วยงานต้องกรอกเบอร์โทรที่ลงทะเบียนด้วย)",
      "ระบบส่งรหัส **OTP 6 หลัก** ไปที่อีเมลนั้น (ใช้ได้ 15 นาที)",
      "กรอก OTP แล้วตั้งรหัสผ่านใหม่ได้ทันที"
    ],
    tip: "กรอก OTP ผิดเกิน 5 ครั้ง คำขอจะถูกยกเลิก ต้องขอ OTP ใหม่",
    links: [{ label: "ไปหน้าเข้าสู่ระบบ", to: "/pages/login", roles: ["guest"] }],
    related: ["password_no_otp", "college_reset_password"]
  },
  {
    id: "password_no_otp",
    category: "password",
    title: "ไม่ได้รับอีเมล OTP",
    keywords: ["ไม่ได้รับ otp", "otp ไม่มา", "ไม่ได้รับอีเมล", "อีเมลไม่มา", "ไม่มีอีเมล", "otp", "รหัส otp", "otp ผิด", "otp หมดอายุ", "อีเมลเปลี่ยน", "อีเมลเก่า", "ไม่มีเมล"],
    answer: "หากไม่ได้รับรหัส OTP:",
    steps: [
      "ตรวจในกล่อง **Junk/Spam/Promotions** ของอีเมล",
      "อีเมลที่กรอกต้อง**ตรงกับอีเมลที่บันทึกในระบบ**ทุกตัวอักษร",
      "รอสักครู่แล้วขอ OTP ใหม่ (OTP ใช้ได้ 15 นาที)",
      "อีเมลในระบบเป็นอีเมลเก่า/ไม่ได้ใช้แล้ว → ให้**ผู้ดูแลสถานศึกษา**รีเซ็ตรหัสผ่านให้แทน แล้วแก้อีเมลในข้อมูลส่วนตัวให้เป็นปัจจุบัน"
    ],
    related: ["college_reset_password", "profile_edit"]
  },
  {
    id: "password_change",
    category: "password",
    title: "เปลี่ยนรหัสผ่านของตนเอง",
    keywords: ["เปลี่ยนรหัส", "เปลี่ยนรหัสผ่าน", "change password", "ตั้งรหัสใหม่", "แก้รหัสผ่าน", "รหัสผ่านใหม่", "บังคับเปลี่ยนรหัส", "ให้ตั้งรหัสผ่านใหม่"],
    answer:
      "เปลี่ยนรหัสผ่านได้ที่เมนู**ข้อมูลผู้ใช้งานระบบ (โปรไฟล์)** หลังเข้าสู่ระบบ โดยต้องกรอกรหัสผ่านเดิมยืนยันก่อน\nหากระบบ**บังคับให้ตั้งรหัสผ่านใหม่**ทันทีหลังเข้าสู่ระบบ ถือเป็นขั้นตอนปกติ (เข้าครั้งแรก / ถูกรีเซ็ตรหัส / ระบบปรับปรุงความปลอดภัย)",
    links: [
      { label: "ไปหน้าโปรไฟล์", to: "/UserProfile", roles: SELF },
      { label: "ไปหน้าโปรไฟล์", to: "/userEtc/UserProfile", roles: ["etc"] }
    ],
    related: ["password_policy"]
  },
  {
    id: "password_policy",
    category: "password",
    title: "เงื่อนไขรหัสผ่านที่ใช้ได้",
    keywords: ["รหัสผ่านต้อง", "กี่ตัว", "เงื่อนไขรหัสผ่าน", "รหัสผ่านไม่ผ่าน", "รหัสผ่านไม่ปลอดภัย", "รหัสผ่านอ่อน", "ตั้งรหัสไม่ได้", "password policy", "รหัสผ่านที่ดี"],
    answer:
      "รหัสผ่านใหม่ต้องมี**อย่างน้อย 8 ตัวอักษร** และมี**ทั้งตัวอักษรและตัวเลข**\nแนะนำ: ผสมตัวพิมพ์ใหญ่-เล็กและสัญลักษณ์ ไม่ใช้เลขบัตรประชาชน วันเกิด หรือรหัสเดิมซ้ำ",
    related: ["password_change"]
  },
  {
    id: "college_reset_password",
    category: "password",
    title: "สถานศึกษารีเซ็ตรหัสผ่านให้บุคลากร",
    keywords: ["รีเซ็ตรหัสให้ครู", "รีเซ็ตให้บุคลากร", "reset password ให้", "รีเซ็ตรหัสผ่านบุคลากร", "ครูลืมรหัส", "บุคลากรลืมรหัส", "ขอรหัสผ่านให้ครู", "รีเซ็ตรหัสครู", "ครู ลืมรหัส", "บุคลากร ลืมรหัส", "รีเซ็ต ให้ครู", "รีเซ็ต ให้บุคลากร"],
    roles: ["B"],
    answer: "ผู้ดูแลสถานศึกษารีเซ็ตรหัสผ่านให้บุคลากรของสถานศึกษา**ตนเอง**ได้:",
    steps: [
      "เข้าเมนู **\"ข้อมูล ข้าราชการ ลูกจ้างประจำ พนักงานราชการ ครูอัตราจ้าง\"**",
      "กดปุ่ม **Reset Password** ที่รายชื่อบุคลากร → ยืนยัน",
      "ระบบสุ่มรหัสผ่านใหม่และแสดง**เพียงครั้งเดียว** → กด \"คัดลอกรหัสผ่าน\" แล้วแจ้งเจ้าตัว",
      "บุคลากรจะถูกบังคับให้เปลี่ยนรหัสผ่านทันทีเมื่อเข้าสู่ระบบครั้งถัดไป"
    ],
    tip: "รีเซ็ตให้บุคลากรของสถานศึกษาอื่นไม่ได้ และทุกการรีเซ็ตถูกบันทึกประวัติ (ไม่บันทึกตัวรหัสผ่าน) — ส่งรหัสให้เจ้าตัวโดยตรง ห้ามโพสต์ในกลุ่มแชท",
    links: [{ label: "ไปหน้าข้อมูลบุคลากร", to: "/college/personnel_etc", roles: ["B"] }],
    related: ["college_add_personnel"]
  },

  // ===================== ข้อมูลส่วนตัว =====================
  {
    id: "profile_edit",
    category: "profile",
    title: "แก้ไขข้อมูลส่วนตัว",
    keywords: ["แก้ไขข้อมูล", "แก้ข้อมูล", "ข้อมูลส่วนตัว", "ข้อมูลผิด", "อัปเดตข้อมูล", "อัพเดทข้อมูล", "เปลี่ยนชื่อ", "เปลี่ยนนามสกุล", "เปลี่ยนอีเมล", "เปลี่ยนเบอร์", "ที่อยู่", "โปรไฟล์", "profile", "ประวัติส่วนตัว"],
    roles: [...SELF, "etc"],
    answer:
      "หลังเข้าสู่ระบบ ข้อมูลส่วนตัวแยกเป็นหมวดในเมนูด้านซ้าย: ข้อมูลผู้ใช้งาน, คุณวุฒิการศึกษา, ประสบการณ์, ประวัติการรับราชการ, ผลงาน/รางวัล, วินัย/โทษ, การปฏิบัติหน้าที่, ลูกเสือ\nแนะนำให้ตรวจ**อีเมลและเบอร์โทร**ให้เป็นปัจจุบัน เพราะใช้รับ OTP เมื่อลืมรหัสผ่าน",
    tip: "ข้อมูลบางรายการ (เช่น ตำแหน่ง เลขที่ตำแหน่ง สังกัด) แก้เองไม่ได้ ต้องแจ้งงานบุคลากรของสถานศึกษา",
    links: [
      { label: "ข้อมูลผู้ใช้งาน", to: "/UserProfile", roles: SELF },
      { label: "คุณวุฒิการศึกษา", to: "/personnel_education", roles: SELF },
      { label: "ประวัติการรับราชการ", to: "/personnel_work_history", roles: SELF },
      { label: "ข้อมูลผู้ใช้งาน", to: "/userEtc/UserProfile", roles: ["etc"] },
      { label: "คุณวุฒิการศึกษา", to: "/userEtc/personnel_education", roles: ["etc"] }
    ],
    related: ["profile_sections", "profile_resume"]
  },
  {
    id: "profile_sections",
    category: "profile",
    title: "เพิ่มวุฒิ / ประสบการณ์ / ผลงาน / วินัย",
    keywords: ["วุฒิการศึกษา", "คุณวุฒิ", "เพิ่มวุฒิ", "ปริญญา", "ประสบการณ์", "ผลงาน", "รางวัล", "วินัย", "โทษ", "การปฏิบัติหน้าที่", "หน้าที่พิเศษ", "ประวัติการรับราชการ", "อบรม", "ประวัติการอบรม"],
    roles: [...SELF, "etc"],
    answer: "เลือกหมวดที่ต้องการจากเมนูด้านซ้าย แล้วกดปุ่ม **เพิ่ม** / ไอคอนแก้ไข ในแต่ละรายการ:",
    links: [
      { label: "คุณวุฒิการศึกษา", to: "/personnel_education", roles: SELF },
      { label: "ประสบการณ์", to: "/personnel_experience", roles: SELF },
      { label: "ผลงาน รางวัล", to: "/personnel_award", roles: SELF },
      { label: "การปฏิบัติหน้าที่", to: "/personnel_duty", roles: SELF },
      { label: "วินัย โทษ", to: "/personnel_discipline", roles: SELF },
      { label: "คุณวุฒิการศึกษา", to: "/userEtc/personnel_education", roles: ["etc"] },
      { label: "ประสบการณ์", to: "/userEtc/personnel_experience", roles: ["etc"] },
      { label: "ผลงาน รางวัล", to: "/userEtc/personnel_award", roles: ["etc"] },
      { label: "การปฏิบัติหน้าที่", to: "/userEtc/personnel_duty", roles: ["etc"] }
    ]
  },
  {
    id: "profile_resume",
    category: "profile",
    title: "พิมพ์ประวัติอย่างย่อ",
    keywords: ["พิมพ์ประวัติ", "ประวัติอย่างย่อ", "resume", "เรซูเม่", "ปริ้นประวัติ", "print ประวัติ", "พิมพ์เอกสาร", "ก.พ.7", "กพ7"],
    roles: SELF,
    answer:
      "ข้าราชการครูฯ และพนักงานราชการพิมพ์ **\"ประวัติอย่างย่อ\"** ของตนเองได้จากหน้า **ข้อมูลผู้ใช้งานระบบ** กดปุ่ม **\"พิมพ์ประวัติ\"** ระบบจะแสดงตัวอย่างประวัติฉบับเต็ม (ข้อมูลพื้นฐาน ที่อยู่ คุณวุฒิ ใบอนุญาต ประวัติรับราชการ เครื่องราชฯ ฯลฯ) พร้อมปุ่มพิมพ์และดาวน์โหลด Word ข้อมูลดึงจากข้อมูลส่วนตัวในระบบ จึงควรตรวจสอบให้ครบถ้วนก่อนพิมพ์",
    links: [{ label: "ไปหน้าข้อมูลผู้ใช้งานระบบ", to: "/UserProfile", roles: SELF }],
    related: ["print_problem", "profile_edit"]
  },
  {
    id: "scout_update",
    category: "profile",
    title: "ปรับข้อมูลวุฒิลูกเสือ",
    keywords: ["ลูกเสือ", "วุฒิลูกเสือ", "ลูกเสือวิสามัญ", "scout", "กิจกรรมลูกเสือ", "ข้อมูลลูกเสือ"],
    roles: [...SELF, "etc"],
    answer:
      "บุคลากรที่มีข้อมูลลูกเสือวิสามัญอาชีวศึกษา ปรับปรุงข้อมูลได้ที่เมนู **\"ข้อมูลลูกเสือวิสามัญอาชีวศึกษา\"** หลังเข้าสู่ระบบ ภายในช่วงเวลาที่ประกาศบนหน้าแรกของระบบ",
    links: [
      { label: "ข้อมูลลูกเสือ", to: "/personnel_self_scout", roles: SELF },
      { label: "ข้อมูลลูกเสือ", to: "/userEtc/personnel_self_scout", roles: ["etc"] },
      { label: "สถิติข้อมูลลูกเสือ", to: "/pages/personnel_scout_report" }
    ]
  },
  {
    id: "license_info",
    category: "profile",
    title: "ใบอนุญาตประกอบวิชาชีพ",
    keywords: ["ใบอนุญาต", "ใบประกอบวิชาชีพ", "ใบประกอบ", "ใบอนุญาตประกอบวิชาชีพ", "license", "ใบอนุญาตเฉพาะ", "สำรวจใบอนุญาต", "คุรุสภา"],
    answer:
      "ข้อมูลใบอนุญาตประกอบวิชาชีพ และแบบสำรวจใบอนุญาตประกอบวิชาชีพเฉพาะ อยู่ในเมนู **\"สำรวจใบอนุญาตประกอบวิชาชีพเฉพาะ\"** (บุคลากร) และเมนูข้อมูลใบอนุญาตของสถานศึกษา",
    links: [
      { label: "สำรวจใบอนุญาตฯ", to: "/servey_assistant_specialized_licenses", roles: SELF },
      { label: "สำรวจใบอนุญาตฯ (สถานศึกษา)", to: "/college/servey_assistant_specialized_licenses", roles: ["B"] },
      { label: "ข้อมูลใบอนุญาตฯ (สถานศึกษา)", to: "/college/personnel_temporarylicence", roles: ["B"] }
    ]
  },

  // ===================== การย้าย =====================
  {
    id: "transfer_teacher_apply",
    category: "transfer",
    title: "ยื่นคำร้องขอย้าย (ข้าราชการครู)",
    keywords: ["ยื่นย้าย", "ขอย้าย", "ย้ายครู", "คำร้องขอย้าย", "สมัครย้าย", "การย้าย", "ย้ายสายการสอน", "ย้ายสายสนับสนุน", "ย้ายกลับบ้าน", "ยื่นคำร้อง", "transfer", "ย้ายยังไง"],
    roles: ["teacher"],
    answer:
      "ข้าราชการครูยื่นคำร้องขอย้ายผ่านเมนู **\"การย้ายสายการสอน\"** ได้**เฉพาะช่วงเวลาที่ส่วนกลางเปิดรับ**ตามประกาศ:",
    steps: [
      "ตรวจสอบประกาศ/หลักเกณฑ์การย้ายและกำหนดการที่หน้าข่าวประชาสัมพันธ์",
      "ตรวจข้อมูลที่ใช้คำนวณคะแนนให้ถูกต้องก่อนยื่น: **อายุราชการในสาขาวิชาเอก, ระยะเวลาปฏิบัติหน้าที่สอนในสถานศึกษาปัจจุบัน, สภาพความยากลำบาก**",
      "เข้าเมนูการย้าย เลือกสถานศึกษา/สาขาวิชาที่ประสงค์ย้าย แล้วบันทึก",
      "พิมพ์แบบคำร้อง ลงนาม และยื่นต่อสถานศึกษาต้นสังกัดตามขั้นตอนในประกาศ"
    ],
    tip: "กรณีพิเศษ (เช่น ติดตามคู่สมรส/ดูแลบิดามารดา ตามหลักเกณฑ์) ใช้เมนู \"การย้ายสายการสอนกรณีพิเศษ\"",
    links: [
      { label: "การย้ายสายการสอน", to: "/transference_personnel", roles: ["teacher"] },
      { label: "การย้ายกรณีพิเศษ", to: "/transference_personnel_sp", roles: ["teacher"] },
      { label: "อายุราชการในสาขาวิชาเอก", to: "/service_age", roles: ["teacher"] },
      { label: "ประกาศ/หลักเกณฑ์", to: "/news" }
    ],
    related: ["transfer_result", "transfer_manage_apply", "transfer_38"]
  },
  {
    id: "transfer_38",
    category: "transfer",
    title: "การย้ายบุคลากรฯ 38 ค.(2)",
    keywords: ["38 ค", "38ค", "38 ค.(2)", "มาตรา 38", "บุคลากรทางการศึกษาอื่น 38", "สายสนับสนุน 38", "38ค2"],
    answer:
      "การย้ายบุคลากรทางการศึกษาอื่นตามมาตรา **38 ค.(2)** มีรอบและกำหนดการแยกจากการย้ายครูสายการสอน สถานศึกษาใช้เมนู \"การย้าย 38 ค.(2)\" และ \"ผู้ประสงค์ขอย้ายเข้า 38 ค.(2)\" ในการรวบรวม/พิจารณาคำร้อง ตามกำหนดการที่ประกาศ",
    links: [
      { label: "การย้าย 38 ค.(2)", to: "/college/transference_personnel_38", roles: ["B"] },
      { label: "ผู้ประสงค์ย้ายเข้า 38 ค.(2)", to: "/college/transference_personnel_movein_38", roles: ["B"] },
      { label: "ประกาศ/กำหนดการ", to: "/news" }
    ]
  },
  {
    id: "transfer_manage_apply",
    category: "transfer",
    title: "การย้ายผู้บริหาร (ผอ./รอง ผอ.)",
    keywords: ["ย้ายผู้บริหาร", "ย้ายสายบริหาร", "ย้ายผอ", "ย้าย ผอ.", "ย้ายรองผอ", "ย้ายรอง ผอ.", "ผู้อำนวยการย้าย", "รองผู้อำนวยการย้าย"],
    roles: ["teacher"],
    answer:
      "ผู้อำนวยการ/รองผู้อำนวยการ ยื่นคำร้องขอย้ายผ่านเมนู **\"การย้ายสายบริหาร\"** ตามกำหนดการที่ส่วนกลางประกาศ (ตำแหน่งผู้อำนวยการมีเมนูแยก)\nสถานศึกษาปลายทางดูผู้ประสงค์ย้ายเข้าได้ที่เมนู \"การย้ายสายบริหาร สถานศึกษาอื่นประสงค์ย้ายเข้า\"",
    links: [
      { label: "การย้ายสายบริหาร", to: "/transference_manage", roles: ["teacher"] },
      { label: "ย้ายตำแหน่ง ผอ.", to: "/transference_manage_d", roles: ["teacher"] },
      { label: "การย้ายสายบริหาร (สถานศึกษา)", to: "/college/transference_manage", roles: ["B"] },
      { label: "ประกาศ/กำหนดการ", to: "/news" }
    ]
  },
  {
    id: "transfer_govem_apply",
    category: "transfer",
    title: "ย้ายพนักงานราชการ",
    keywords: ["ย้ายพนักงานราชการ", "พนักงานราชการย้าย", "ขอย้ายพนักงานราชการ", "ย้ายพนง", "พนักงานราชการ ย้าย", "พนักงานราชการ ขอย้าย"],
    roles: ["gov_em", "B"],
    answer:
      "พนักงานราชการยื่นคำร้องขอย้ายผ่านเมนู **\"การย้าย พนักงานราชการ\"** ในช่วงเวลาที่ส่วนกลางกำหนด\nสถานศึกษารวบรวมคำร้องที่เมนู \"รวบรวมคำร้องขอย้าย ตำแหน่ง พนักงานราชการ\" และรับรายงานตัวเมื่อย้ายสำเร็จที่ \"รับรายงานตัว พนักงานราชการ กรณีย้าย\"",
    links: [
      { label: "การย้ายพนักงานราชการ", to: "/transference_govem", roles: ["gov_em"] },
      { label: "รวบรวมคำร้อง (สถานศึกษา)", to: "/college/transference_personnel_govem", roles: ["B"] },
      { label: "รับรายงานตัว (สถานศึกษา)", to: "/college/gov_em/receive_govem", roles: ["B"] },
      { label: "ประกาศ/กำหนดการ", to: "/news" }
    ]
  },
  {
    id: "transfer_result",
    category: "transfer",
    title: "ผลการย้าย / รายงานตัวหลังย้าย",
    keywords: ["ผลการย้าย", "ประกาศผลย้าย", "ย้ายได้ไหม", "ย้ายสำเร็จ", "ผลย้าย", "รายงานตัวครูย้าย", "รายงานตัวหลังย้าย", "ได้ย้าย", "ผลประมวลผลย้าย"],
    answer:
      "ผลการย้ายประกาศตามกำหนดการของส่วนกลาง (ดูหน้าข่าวประชาสัมพันธ์) เมื่อย้ายสำเร็จ สถานศึกษาปลายทางบันทึก**รับรายงานตัว**ในเมนู \"รายงานตัวครูย้าย\" (หรือ \"รายงานตัว พนักงานราชการ\")",
    links: [
      { label: "ประกาศผล", to: "/news" },
      { label: "รายงานตัวครูย้าย", to: "/college/conditons_transfer_success", roles: ["B"] },
      { label: "รายงานตัวพนักงานราชการ", to: "/college/conditons_transfer_success_gov", roles: ["B"] }
    ]
  },
  {
    id: "college_transfer_conditions",
    category: "transfer",
    title: "สถานศึกษากำหนดเงื่อนไขการรับย้าย",
    keywords: ["เงื่อนไขการย้าย", "เงือนไขการย้าย", "เงื่อนไขรับย้าย", "สาขาที่รับย้าย", "ตำแหน่งว่างรับย้าย", "เปิดรับย้าย", "ผู้ประสงค์ย้ายเข้า", "รับย้าย"],
    roles: ["B"],
    answer:
      "สถานศึกษากำหนดสาขาวิชา/จำนวนที่ต้องการรับย้ายในเมนู **\"เงื่อนไขการย้าย\"** ตามช่วงเวลาที่เปิด และดูรายชื่อผู้ที่ประสงค์ย้ายเข้าได้ที่ \"ผู้ประสงค์ขอย้ายเข้า\" ประวัติการกำหนดเงื่อนไขย้อนหลังดูได้ที่ \"ประวัติข้อมูลเงื่อนไขการย้าย\"",
    links: [
      { label: "เงื่อนไขการย้าย", to: "/college/conditions_branch", roles: ["B"] },
      { label: "ผู้ประสงค์ขอย้ายเข้า", to: "/college/transference_personnel_movein", roles: ["B"] },
      { label: "การย้ายสายการสอน (สถานศึกษา)", to: "/college/transference_personnel", roles: ["B"] }
    ]
  },
  {
    id: "unit_transfer_review",
    category: "transfer",
    title: "สอจ./สถาบัน พิจารณาคำร้องขอย้าย",
    keywords: ["ตรวจสอบคำร้องย้าย", "พิจารณาย้าย", "คณะกรรมการตรวจสอบผู้ประสงค์ขอย้าย", "กรรมการย้าย", "สอจ ย้าย", "สถาบัน ย้าย"],
    roles: ["C", "G", "S"],
    answer:
      "บัญชีสถาบัน/สอจ. ดูภาพรวมคำร้องขอย้ายในพื้นที่ได้ที่เมนู \"การย้ายสายการสอนและสายสนับสนุน\" และ \"การย้ายสายบริหาร\" และกำหนดรายชื่อคณะกรรมการตรวจสอบผู้ประสงค์ขอย้ายที่เมนู \"คณะกรรมการตรวจสอบผู้ประสงค์ขอย้าย\"",
    links: [
      { label: "คำร้องขอย้าย", to: "/vecprovince/transference_personnel", roles: ["G"] },
      { label: "คณะกรรมการตรวจสอบ", to: "/vecprovince/commiteevecpro", roles: ["G"] },
      { label: "คำร้องขอย้าย", to: "/iven/transference_personnel", roles: ["C"] },
      { label: "คณะกรรมการตรวจสอบ", to: "/iven/commiteevecpro", roles: ["C"] },
      { label: "คำร้องขอย้าย", to: "/supervisions/transference_personnel", roles: ["S"] }
    ]
  },

  // ===================== ครูผู้ช่วย =====================
  {
    id: "assistant_report",
    category: "assistant",
    title: "รายงานตัวครูผู้ช่วย / เลือกสถานศึกษา",
    keywords: ["รายงานตัว", "ครูผู้ช่วย", "บรรจุใหม่", "เลือกสถานศึกษา", "รายงานตัวครูผู้ช่วย", "เลือกวิทยาลัย", "ผู้สอบผ่าน", "ขึ้นบัญชี", "เรียกบรรจุ", "แสดงเจตจำนง"],
    answer:
      "ผู้ได้รับการบรรจุตำแหน่ง **ครูผู้ช่วย** รายงานตัวและเลือกสถานศึกษาผ่านเว็บไซต์ตามกำหนดการที่ประกาศ:",
    steps: [
      "กรอกข้อมูลรายงานตัว (ก่อนเลือกสถานศึกษา) ให้ครบถ้วน",
      "เข้าสู่ระบบ \"รายงานตัวเลือกสถานศึกษา ตำแหน่งครูผู้ช่วย\" ตามลำดับที่ประกาศ",
      "เลือกสถานศึกษาจากตำแหน่งว่างที่เปิดให้เลือก แล้วยืนยัน",
      "ไปรายงานตัวที่สถานศึกษา — สถานศึกษาบันทึกรับรายงานตัวในระบบ"
    ],
    links: [
      { label: "รายงานตัว (ก่อนเลือกสถานศึกษา)", to: "/pages/manage_assistant_teacher" },
      { label: "เลือกสถานศึกษา", to: "/pages/manage_college_manpower" },
      { label: "รับรายงานตัว (สถานศึกษา)", to: "/college/manage_assistant_teacher", roles: ["B"] }
    ],
    related: ["assistant_committee"]
  },
  {
    id: "assistant_committee",
    category: "assistant",
    title: "แต่งตั้งกรรมการประเมินครูผู้ช่วย",
    keywords: ["กรรมการประเมิน", "ประเมินครูผู้ช่วย", "แต่งตั้งกรรมการ", "ผู้ทรงคุณวุฒิ", "กรรมการภายนอก", "ครูพี่เลี้ยง", "เตรียมความพร้อม", "พัฒนาอย่างเข้ม", "ผลการประเมินครูผู้ช่วย", "ประเมินครั้งที่"],
    roles: ["B", "teacher"],
    answer:
      "สถานศึกษากำหนดคณะกรรมการประเมินครูผู้ช่วยในระบบ:",
    steps: [
      "บันทึกข้อมูล **ผู้ทรงคุณวุฒิจากภายนอกสถานศึกษา** ก่อน (เมนู \"ข้อมูลผู้ทรงคุณวุฒิอื่นจากภายนอกสถานศึกษา\")",
      "กำหนดรายชื่อกรรมการต่อครูผู้ช่วยแต่ละราย (เมนู \"กำหนดรายชื่อกรรมการต่อครูผู้ช่วย\")",
      "บันทึก/ส่งผลการประเมินในเมนู \"ครูผู้ช่วย\" ตามรอบการประเมิน"
    ],
    tip: "ครูผู้ช่วยตรวจสอบข้อมูลการประเมินของตนเองได้ที่เมนู \"ข้อมูลประเมินครูผู้ช่วย\"",
    links: [
      { label: "ผู้ทรงคุณวุฒิภายนอก", to: "/college/assistantcommitteequalified", roles: ["B"] },
      { label: "กำหนดกรรมการ", to: "/college/assistantcommittee", roles: ["B"] },
      { label: "ครูผู้ช่วย", to: "/college/assistant_teacher", roles: ["B"] },
      { label: "ข้อมูลประเมินครูผู้ช่วย", to: "/assistant_teacher", roles: ["teacher"] }
    ]
  },

  // ===================== สอบคัดเลือก =====================
  {
    id: "exam_apply",
    category: "exam",
    title: "สมัครสอบผู้บริหาร / ศึกษานิเทศก์",
    keywords: ["สมัครสอบ", "สอบผู้บริหาร", "สอบรองผอ", "สอบรอง ผอ.", "สอบผอ", "สอบ ผอ.", "ศึกษานิเทศก์", "สอบศึกษานิเทศก์", "คัดเลือกผู้บริหาร", "สมัครสอบรอง", "ภาค ก", "ภาค ข", "ภาค ค"],
    roles: ["teacher"],
    answer:
      "สมัครคัดเลือกตำแหน่งผู้บริหารสถานศึกษาและศึกษานิเทศก์ผ่านเมนูหลังเข้าสู่ระบบ **ในช่วงเวลาที่ประกาศรับสมัคร** เท่านั้น การประเมินแบ่งเป็น ภาค ก. ภาค ข. และภาค ค. (สัมภาษณ์) ตามประกาศแต่ละรอบ",
    links: [
      { label: "สมัคร รอง ผอ./ผู้บริหาร", to: "/sedirector_app", roles: ["teacher"] },
      { label: "สมัคร ผอ.วิทยาลัย", to: "/director_app", roles: ["teacher"] },
      { label: "สมัคร ศึกษานิเทศก์", to: "/supervisor_apps", roles: ["teacher"] },
      { label: "ประกาศ/กำหนดการสอบ", to: "/news" }
    ],
    related: ["exam_report_choose"]
  },
  {
    id: "exam_report_choose",
    category: "exam",
    title: "รายงานตัวเลือกสถานศึกษา (ผู้บริหาร)",
    keywords: ["รายงานตัวผู้บริหาร", "เลือกสถานศึกษาผู้บริหาร", "ผู้ผ่านการคัดเลือก", "รายงานตัว ผอ", "รายงานตัว รอง ผอ", "อัตราว่างผู้บริหาร"],
    answer:
      "ผู้ผ่านการคัดเลือกตำแหน่งผู้บริหารสถานศึกษา รายงานตัวเลือกสถานศึกษาได้ที่หน้า **\"รายงานตัวเลือกสถานศึกษา ตำแหน่งผู้บริหารสถานศึกษา\"** ตามกำหนดการ และสถานศึกษาบันทึกรับรายงานตัวในเมนูของสถานศึกษา",
    links: [
      { label: "รายงานตัวเลือกสถานศึกษา", to: "/pages/sedirector_app_choose" },
      { label: "รับรายงานตัว (สถานศึกษา)", to: "/college/sedirector_app_report", roles: ["B"] }
    ]
  },
  {
    id: "exam_unit_assess",
    category: "exam",
    title: "หน่วยงานประเมินผู้สมัคร ภาค ก./ข.",
    keywords: ["ประเมินภาค ก", "ประเมินภาค ข", "รายงานผลการประเมิน", "ผู้สมัครสอบ", "ประเมินผู้สมัคร", "ให้คะแนนผู้สมัคร"],
    roles: ["B", "C", "G", "S"],
    answer:
      "หน่วยงานที่มีหน้าที่ประเมินผู้สมัคร (สถานศึกษา ภาค ก. / สอจ. และสถาบัน ภาค ก.-ข.) บันทึกผลในเมนูผู้สมัครสอบของหน่วยงานตนเอง ตามกำหนดการของแต่ละรอบ",
    links: [
      { label: "ประเมินภาค ก (สถานศึกษา)", to: "/college/sedirector_app", roles: ["B"] },
      { label: "รายงานผลภาค ก", to: "/vecprovince/sedirector_app_a/", roles: ["G"] },
      { label: "รายงานผลภาค ข", to: "/vecprovince/sedirector_app_b/", roles: ["G"] },
      { label: "ผู้สมัครสอบ", to: "/iven/sedirector_app/", roles: ["C"] },
      { label: "ประเมิน ผอ. ภาค ข", to: "/iven/director_app_b/", roles: ["C"] },
      { label: "ผู้สมัครสอบ", to: "/supervisions/sedirector_app/", roles: ["S"] }
    ]
  },

  // ===================== พนักงานราชการ =====================
  {
    id: "govem_assessment_history",
    category: "govem",
    title: "ผลการปฏิบัติงานพนักงานราชการ",
    keywords: ["ผลการปฏิบัติงาน", "ประเมินพนักงานราชการ", "ผลประเมินย้อนหลัง", "ประเมินผลงาน", "ต่อสัญญา", "สัญญาจ้าง", "หมดสัญญา"],
    roles: ["gov_em", "B"],
    answer:
      "พนักงานราชการดูผลการปฏิบัติงานย้อนหลังของตนเองได้ที่เมนู **\"ข้อมูลผลการปฏิบัติงานย้อนหลัง\"** ส่วนสถานศึกษาบันทึกผลประเมินที่เมนู \"ประเมิน พนักงานราชการ\" และจัดการข้อมูลสัญญาที่ \"ข้อมูลพนักงานราชการ และสัญญา\"",
    links: [
      { label: "ผลการปฏิบัติงานย้อนหลัง", to: "/personnel_govem_assesment", roles: ["gov_em"] },
      { label: "ประเมินพนักงานราชการ", to: "/college/gov_em/assessment_govem", roles: ["B"] },
      { label: "ข้อมูลพนักงานราชการและสัญญา", to: "/college/gov_em/personnel_govem", roles: ["B"] }
    ],
    related: ["govem_unit"]
  },
  {
    id: "govem_unit",
    category: "govem",
    title: "งานพนักงานราชการของสถานศึกษา",
    keywords: ["พนักงานราชการ", "กรอบพนักงานราชการ", "กรอบอัตราพนักงานราชการ", "ค่าตอบแทน", "คำนวณค่าตอบแทน", "เบิกจ่าย", "เงินเดือนพนักงานราชการ", "พิมพ์สัญญาจ้าง", "ลาออก", "ประวัติลาออก"],
    roles: ["B", "C", "A"],
    answer: "งานพนักงานราชการของสถานศึกษาอยู่ในกลุ่มเมนู **\"พนักงานราชการ\"**:",
    steps: [
      "กรอบอัตรา: ข้อมูลพนักงานราชการ กรอบอัตรา",
      "ข้อมูลรายบุคคลและสัญญา / พิมพ์แบบสัญญาจ้าง",
      "ประเมินผลการปฏิบัติงาน",
      "การคำนวณค่าตอบแทนพนักงานราชการทั่วไป",
      "ประวัติการลาออก"
    ],
    links: [
      { label: "กรอบอัตรา", to: "/college/gov_em/government_employee", roles: ["B"] },
      { label: "ข้อมูลและสัญญา", to: "/college/gov_em/personnel_govem", roles: ["B"] },
      { label: "คำนวณค่าตอบแทน", to: "/college/gov_em/cal_compensation", roles: ["B"] },
      { label: "ประวัติการลาออก", to: "/college/gov_em/ResignHistoryPage", roles: ["B"] },
      { label: "กรอบอัตรา", to: "/iven/gov_em/government_employee", roles: ["C"] },
      { label: "กรอบอัตรากำลังพนักงานราชการ", to: "/admin/gov_em/government_employee", roles: ["A"] }
    ]
  },

  // ===================== อัตรากำลัง =====================
  {
    id: "manpower_report",
    category: "manpower",
    title: "รายงานอัตรากำลังสถานศึกษา",
    keywords: ["อัตรากำลัง", "รายงานอัตรากำลัง", "ประมวลผลอัตรากำลัง", "ขาดเกิน", "ครูขาด", "ครูเกิน", "จำนวนนักเรียน", "จำนวนห้อง", "หมวดวิชา", "rate workforce", "จ.18", "จ18", "เลขที่ตำแหน่ง", "ตำแหน่งว่าง"],
    roles: ["B", "C", "G", "S", "A"],
    answer: "การรายงานอัตรากำลังสถานศึกษา ทำตามลำดับ:",
    steps: [
      "**ข้อมูลทั่วไป** ของสถานศึกษา",
      "**รายงานข้อมูลสถานศึกษา** — จำนวนนักเรียน/ห้องเรียนตามหมวดวิชา/สาขา",
      "**ประมวลผลอัตรากำลัง** แล้วตรวจสอบผลขาด/เกิน",
      "ตรวจรายงานสรุปก่อนยืนยันส่ง",
      "กำหนดสาขาวิชาสำหรับเลขที่ตำแหน่งว่าง (ถ้ามี)"
    ],
    tip: "ยืนยันส่งแล้วแก้ไขไม่ได้ ต้องให้ผู้ดูแลระบบยกเลิกการยืนยันให้ก่อน",
    links: [
      { label: "ข้อมูลทั่วไป", to: "/college/rate_workforce_g", roles: ["B"] },
      { label: "รายงานข้อมูลสถานศึกษา", to: "/college/rate_workforce", roles: ["B"] },
      { label: "ประมวลผลอัตรากำลัง", to: "/college/rate_workforce_cal", roles: ["B"] },
      { label: "สาขาวิชาตำแหน่งว่าง", to: "/college/man_power_j", roles: ["B"] },
      { label: "รายงานอัตรากำลัง", to: "/iven/rate_workforce_report", roles: ["C"] },
      { label: "รายงานอัตรากำลัง", to: "/vecprovince/rate_workforce_report", roles: ["G"] },
      { label: "รายงานอัตรากำลัง", to: "/supervisions/rate_workforce_report", roles: ["S"] },
      { label: "จ.18", to: "/admin/jor18", roles: ["A"] },
      { label: "อัตรากำลังขาด/เกิน", to: "/admin/man_powersummary", roles: ["A"] },
      { label: "อัตรากำลังสถานศึกษา (สาธารณะ)", to: "/pages/manPower" }
    ]
  },

  // ===================== งานสถานศึกษา =====================
  {
    id: "college_add_personnel",
    category: "college",
    title: "เพิ่ม/นำเข้าข้อมูลบุคลากร",
    keywords: ["เพิ่มบุคลากร", "เพิ่มครู", "เพิ่มข้อมูลบุคลากร", "นำเข้าข้อมูล", "import", "excel", "เอ็กเซล", "อัปโหลดรายชื่อ", "ลูกจ้างใหม่", "ครูอัตราจ้างใหม่", "บุคลากรใหม่", "ลบบุคลากร", "ย้ายออก", "เกษียณ", "พ้นจากราชการ"],
    roles: ["B"],
    answer:
      "ข้อมูลบุคลากรของสถานศึกษาจัดการที่เมนู **\"ข้อมูล ข้าราชการ ลูกจ้างประจำ พนักงานราชการ ครูอัตราจ้างหรือลูกจ้างชั่วคราว\"** — เพิ่มทีละรายการ หรือนำเข้าเป็นชุดด้วยฟอร์มนำเข้าข้อมูล (Excel)",
    tip: "ข้อมูลข้าราชการครู (ตำแหน่ง/เลขที่ตำแหน่ง/การย้ายเข้า-ออก) บางรายการต้องให้ส่วนกลางปรับให้ เพื่อให้ตรงกับคำสั่ง",
    links: [
      { label: "ข้อมูลบุคลากรสถานศึกษา", to: "/college/personnel_etc", roles: ["B"] },
      { label: "ข้อมูลข้าราชการครูฯ", to: "/college/personnel_tem", roles: ["B"] },
      { label: "ฟอร์มนำเข้าข้อมูล", to: "/college/personnel_form_import/", roles: ["B"] }
    ],
    related: ["college_reset_password", "college_responsible_person"]
  },
  {
    id: "college_info",
    category: "college",
    title: "แก้ไขข้อมูลสถานศึกษา",
    keywords: ["ข้อมูลสถานศึกษา", "ข้อมูลวิทยาลัย", "แก้ไขข้อมูลวิทยาลัย", "ที่อยู่วิทยาลัย", "โลโก้", "พิกัด", "แผนที่", "รายละเอียดสถานศึกษา", "ข้อมูลหน่วยงาน"],
    roles: UNIT,
    answer: "แก้ไขรายละเอียดหน่วยงานได้ที่เมนู **\"รายละเอียดสถานศึกษา\"** (สอจ./สถาบัน ใช้เมนูข้อมูลเบื้องต้นของหน่วยงาน)",
    links: [
      { label: "รายละเอียดสถานศึกษา", to: "/college/collegeinfo", roles: ["B"] },
      { label: "ข้อมูลสถาบัน", to: "/iven/iveninfo", roles: ["C"] },
      { label: "ข้อมูล สอจ.", to: "/vecprovince/provincialinfo", roles: ["G"] },
      { label: "ค้นหาสถานศึกษา (สาธารณะ)", to: "/pages/colleges" }
    ]
  },
  {
    id: "unit_personnel_view",
    category: "college",
    title: "ดูข้อมูลบุคลากรในพื้นที่",
    keywords: ["ข้อมูลบุคลากรในจังหวัด", "ข้อมูลบุคลากรในสถาบัน", "ดูข้อมูลครู", "ค้นหาบุคลากร", "รายชื่อบุคลากร", "สถิติบุคลากร", "ค้นหาครู"],
    roles: ["C", "G", "S", "A"],
    answer: "บัญชีสถาบัน/สอจ./ศึกษานิเทศก์ ดูข้อมูลบุคลากรของสถานศึกษาในความรับผิดชอบได้ที่เมนูข้อมูลบุคลากร (สิทธิ์ดูตามขอบเขตพื้นที่ของบัญชี)",
    links: [
      { label: "ข้อมูลบุคลากร", to: "/iven/personnel_iven/", roles: ["C"] },
      { label: "ข้อมูลข้าราชการครูฯ", to: "/vecprovince/personnel_tem", roles: ["G"] },
      { label: "ข้อมูลข้าราชการครูฯ", to: "/supervisions/personnel_tem", roles: ["S"] },
      { label: "ข้อมูลข้าราชการครูฯ", to: "/admin/personnel_tem", roles: ["A"] },
      { label: "ค้นหาข้อมูลบุคลากร", to: "/pages/personnel_search" }
    ]
  },
  {
    id: "print_problem",
    category: "general",
    title: "พิมพ์เอกสาร / PDF ไม่ออก",
    keywords: ["พิมพ์ไม่ได้", "ปริ้นไม่ได้", "ปริ้นท์ไม่ได้", "pdf ไม่ขึ้น", "pdf", "พิมพ์เอกสาร", "print", "ดาวน์โหลดไม่ได้", "ป๊อปอัพ", "popup", "ฟอนต์เพี้ยน", "ภาษาไทยเพี้ยน", "ตัวหนังสือเพี้ยน"],
    answer: "หากพิมพ์เอกสารหรือเปิด PDF ไม่ได้:",
    steps: [
      "อนุญาต **Pop-up** สำหรับเว็บไซต์ hrvec.ovec.go.th (ไอคอนที่แถบที่อยู่ของเบราว์เซอร์)",
      "รอให้หน้าเอกสารโหลดครบก่อนสั่งพิมพ์ (Ctrl + P)",
      "ตั้งค่าหน้ากระดาษ A4 และเปิด \"กราฟิกพื้นหลัง (Background graphics)\"",
      "ฟอนต์ภาษาไทยเพี้ยน → กด Ctrl + F5 แล้วลองใหม่ด้วย Chrome หรือ Edge"
    ],
    related: ["login_browser"]
  },

  // ===================== ผู้ดูแลระบบ =====================
  {
    id: "admin_user_manage",
    category: "admin",
    title: "จัดการบัญชีผู้ใช้งาน/สิทธิ์",
    keywords: ["จัดการผู้ใช้", "เพิ่มผู้ใช้", "เพิ่มบัญชี", "ปิดบัญชี", "สิทธิ์ผู้ใช้", "กำหนดสิทธิ์", "สิทธิ์เสริม", "user admin", "useradmin", "บัญชีหน่วยงาน", "รีเซ็ตรหัสวิทยาลัย"],
    roles: ["A"],
    answer:
      "ผู้ดูแลระบบจัดการบัญชีหน่วยงานทุกระดับ (A/B/C/G/S) ที่เมนู **\"จัดการผู้ดูแลระบบ\"** — เพิ่ม/แก้ไข/ปิดการใช้งาน และกำหนดสิทธิ์เสริมเฉพาะทาง (ย้ายครู, ย้ายผู้บริหาร, อัตรากำลัง, ครูผู้ช่วย, พนักงานราชการ ฯลฯ)",
    tip: "สิทธิ์ SuperAdmin (Security Monitor) กำหนดได้โดยผู้ดูแลฐานข้อมูลเท่านั้น",
    links: [
      { label: "จัดการผู้ดูแลระบบ", to: "/admin/userAdmin", roles: ["A"] },
      { label: "ประวัติการเข้าใช้งาน", to: "/admin/data_syslog", roles: ["A"] }
    ]
  },
  {
    id: "admin_timeline",
    category: "admin",
    title: "ตั้งกำหนดการ/เปิด-ปิดระบบ",
    keywords: ["กำหนดการ", "เปิดระบบ", "ปิดระบบ", "ช่วงเวลา", "timeline", "ปฏิทิน", "เปิดรับย้าย", "ปิดรับย้าย", "ตั้งค่าระบบ", "period", "รอบการย้าย"],
    roles: ["A"],
    answer: "ช่วงเวลาเปิด-ปิดของแต่ละงานตั้งค่าในเมนูกำหนดการ:",
    links: [
      { label: "กำหนดการย้ายครู", to: "/admin/timelines/transfer_teachers", roles: ["A"] },
      { label: "กำหนดการย้าย 38 ค.(2)", to: "/admin/timelines/transfer_38", roles: ["A"] },
      { label: "กำหนดการย้ายสายบริหาร", to: "/admin/timelines/transfer_directors", roles: ["A"] },
      { label: "กำหนดการย้ายพนักงานราชการ", to: "/admin/timelines/transfer_govem", roles: ["A"] },
      { label: "กำหนดการรายงานตัวครูผู้ช่วย", to: "/admin/periodassteach", roles: ["A"] },
      { label: "กำหนดการสถานศึกษา", to: "/admin/period_college", roles: ["A"] },
      { label: "กำหนดการลูกเสือ", to: "/admin/period_scout", roles: ["A"] },
      { label: "ตั้งค่าระบบ", to: "/admin/setting", roles: ["A"] }
    ]
  },
  {
    id: "admin_transfer_process",
    category: "admin",
    title: "ประมวลผลการย้าย",
    keywords: ["ประมวลผลย้าย", "ประมวลผลการย้าย", "สับเปลี่ยน", "ประมวลผลแบบที่", "คงค้าง", "สรุปผลการย้าย", "คำสั่งแต่งตั้งรักษาการ", "auto match"],
    roles: ["A"],
    answer: "เมนูประมวลผลการย้าย (สายการสอน) เรียงตามขั้นตอน:",
    steps: [
      "ประกอบการพิจารณา / ประมวลผลแบบที่ 1",
      "ประมวลผลแบบที่ 2",
      "ประมวลผลแบบที่ 3 (สับเปลี่ยนตำแหน่ง) / สับเปลี่ยนแบบปกติ",
      "ตรวจประมวลผลคงค้าง",
      "สรุปผลการประมวลผลการย้าย"
    ],
    tip: "พนักงานราชการและสายบริหารมีชุดเมนูประมวลผลแยก",
    links: [
      { label: "ประกอบการพิจารณา", to: "/admin/transference_location_detail", roles: ["A"] },
      { label: "ประมวลผลแบบที่ 2", to: "/admin/process_transfer", roles: ["A"] },
      { label: "ประมวลผลแบบ 3", to: "/admin/process_transfer_switch", roles: ["A"] },
      { label: "คงค้าง", to: "/admin/pending_transfer", roles: ["A"] },
      { label: "สรุปผล", to: "/admin/conditons_transfer_success", roles: ["A"] },
      { label: "สายบริหาร", to: "/admin/transference_manage_main", roles: ["A"] }
    ]
  },
  {
    id: "admin_switch_user",
    category: "admin",
    title: "สวมสิทธิ์ผู้ใช้งาน",
    keywords: ["สวมสิทธิ์", "switch user", "impersonate", "เข้าแทน", "ดูหน้าจอวิทยาลัย", "login แทน"],
    roles: ["A"],
    answer:
      "ผู้ดูแลระบบใช้เมนู **\"สวมสิทธิ์ผู้ใช้งาน\"** เพื่อเข้าดูหน้าจอในมุมมองของบัญชีหน่วยงานที่แจ้งปัญหา ทุกการสวมสิทธิ์ถูกบันทึกประวัติ เมื่อเสร็จแล้วให้กดสิ้นสุดการสวมสิทธิ์ทุกครั้ง",
    links: [{ label: "สวมสิทธิ์ผู้ใช้งาน", to: "/admin/switch_user", roles: ["A"] }]
  },
  {
    id: "admin_security",
    category: "admin",
    title: "ตรวจสอบความปลอดภัยระบบ",
    keywords: ["security monitor", "ความปลอดภัย", "ตรวจสอบความปลอดภัย", "active session", "revoke", "บังคับออกจากระบบ", "ประวัติ login", "login attempts", "ปลดล็อก", "unlock"],
    roles: ["A"],
    answer:
      "บัญชีที่มีสิทธิ์ **SuperAdmin** ใช้หน้า Security Monitor ดู: บัญชีที่ถูกล็อก, ประวัติการพยายามเข้าสู่ระบบ, การเข้าถึงโดยไม่ได้รับอนุญาต และผู้ที่กำลังใช้งาน (บังคับออกจากระบบได้)",
    links: [{ label: "Security Monitor", to: "/admin/security_monitor", roles: ["A"] }]
  },
  {
    id: "admin_chatbot_insights",
    category: "admin",
    title: "จัดการผู้ช่วย HRVEC (Chatbot)",
    keywords: ["สถิติแชทบอท", "คำถามที่ตอบไม่ได้", "chatbot", "แชทบอท", "บอท", "ปรับปรุงคำตอบ", "เพิ่มคำตอบ", "เพิ่มคำถาม", "จัดการแชท", "สอนบอท"],
    roles: ["A"],
    answer:
      "ผู้ดูแลระบบจัดการผู้ช่วย HRVEC ได้เองที่หน้า **\"จัดการผู้ช่วย HRVEC\"** (เมนูเครื่องมือผู้ดูแลระบบ ไอคอนประแจ) โดยไม่ต้องแก้โค้ด:",
    steps: [
      "**คำถามรอคำตอบ** — ดูคำถามที่ผู้ใช้ถามแล้วบอทตอบไม่ได้ เลือกตอบใหม่ / เพิ่มเข้าหัวข้อเดิม / ไม่ต้องตอบ",
      "**คลังคำถาม-คำตอบ** — เพิ่ม แก้ไข ปิดใช้งาน หรือปักหมุดหัวข้อ (รวมหัวข้อที่มากับระบบ)",
      "**ทดสอบถาม-ตอบ** — จำลองถามในฐานะผู้ใช้แต่ละกลุ่มก่อนใช้งานจริง",
      "**ภาพรวม** — สถิติคำถาม และคำตอบที่ผู้ใช้กดว่าไม่ช่วย"
    ],
    links: [{ label: "จัดการผู้ช่วย HRVEC", to: "/admin/chatbot_manage", roles: ["A"] }]
  },

  // ===================== ความปลอดภัย / PDPA =====================
  {
    id: "security_suspect",
    category: "general",
    title: "สงสัยว่ามีคนเข้าบัญชี",
    keywords: ["มีคนเข้าบัญชี", "ถูกแฮก", "โดนแฮก", "hack", "สวมรอย", "บัญชีถูกใช้", "ข้อมูลถูกแก้", "ใครแก้ข้อมูล", "ไม่ได้เป็นคนทำ", "รหัสรั่ว"],
    answer: "ดำเนินการทันที:",
    steps: [
      "**เปลี่ยนรหัสผ่าน**ทันที (หรือใช้ \"ลืมรหัสผ่าน\" หากเข้าไม่ได้)",
      "แจ้งผู้ดูแลระบบของหน่วยงาน/ส่วนกลาง พร้อมวันเวลาที่พบความผิดปกติ ผู้ดูแลสามารถตรวจประวัติและบังคับออกจากระบบได้",
      "ไม่แชร์รหัสผ่านให้ผู้อื่น แม้เป็นเพื่อนร่วมงาน และออกจากระบบทุกครั้งเมื่อใช้เครื่องสาธารณะ"
    ],
    related: ["password_change", "general_contact"]
  },
  {
    id: "privacy",
    category: "general",
    title: "ข้อมูลส่วนบุคคล (PDPA)",
    keywords: ["pdpa", "ข้อมูลส่วนบุคคล", "ความเป็นส่วนตัว", "ใครเห็นข้อมูล", "เปิดเผยข้อมูล", "ลบข้อมูล", "privacy"],
    answer:
      "ข้อมูลในระบบใช้เพื่อการบริหารงานบุคคลของ สอศ. เท่านั้น การเข้าถึงข้อมูลจำกัดตามสิทธิ์ของบัญชี (ตนเอง / สถานศึกษาต้นสังกัด / หน่วยงานกำกับ) และการเข้าถึงข้อมูลสำคัญถูกบันทึกประวัติ\nหากพบข้อมูลของท่านไม่ถูกต้อง ให้แจ้งงานบุคลากรของสถานศึกษาต้นสังกัด",
    tip: "อย่าพิมพ์เลขบัตรประชาชน รหัสผ่าน หรือ OTP ในช่องแชทนี้"
  }
];
